/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { TooltipHost } from '@fluentui/react';
import { IconButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { EntityLabel, ExtractionLabelerTokenStore, PartialEntityLabel } from 'src/customText/verticals/extractionLabeler/types';
import { Label } from 'src/modules/labeling/types/Label';
import { tokenDataAttribute, tokenIndexDataAttribute } from 'src/modules/labeling/utils/labelingConstants';
import { ITokenRendererProps } from 'src/packages/react-text-annotator/lib/esm';

const messages = defineMessages({
    labelingLocked: {
        id: 'labeler.utils.labelingLocked',
        defaultMessage: 'You cannot edit labels while labeling activity is disabled in Language Studio.'
    }
});

const theme = getScopedTheme('ExtractionLabeler');

export const findLabel = (label: Label, labels: EntityLabel[]) =>
    labels.find(l => l.endCharIndex === label.end && l.startCharIndex === label.start && l.entity.id === label.entity.id.toString());

export const getLabelInfoForDecision = (label: Label, labels: EntityLabel[]) => ({
    endCharIndex: label.end,
    startCharIndex: label.start,
    entityId: label.entity.id.toString(),
    labelType: findLabel(label, labels).labelType
});

const onRenderAnnotationDecisionActions = ({
    label,
    index,
    labels,
    projectId,
    onRemoveLabel,
    onConfirmAutoTags,
    isExtractionLabelerDisabled
}: {
    label: Label;
    index: number;
    projectId: string;
    labels: EntityLabel[];
    isExtractionLabelerDisabled: boolean;
    onRemoveLabel: (label: PartialEntityLabel) => void;
    onConfirmAutoTags: (labels: PartialEntityLabel[]) => void;
}) => {
    const entityLabel = findLabel(label, labels);
    const { localize } = useLocalization();
    return (
        <TooltipHost content={isExtractionLabelerDisabled ? localize(messages.labelingLocked) : ''}>
            <Stack tokens={{ childrenGap: 4, padding: '0 0 0 4px' }} horizontal verticalAlign="center">
                <IconButton
                    disabled={isExtractionLabelerDisabled}
                    iconProps={{ iconName: 'Completed' }}
                    styles={{
                        root: { color: theme.acceptTagsButtonColor, padding: 0, height: 'auto', width: 'auto' },
                        icon: { margin: 0 }
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        logButtonClick(
                            getPortalFromUrl().serviceName,
                            'DataLabeling',
                            entityLabel.labelType === 'autoTagged' ? 'AcceptAutolabel' : 'AcceptGptLabel',
                            null,
                            projectId
                        );
                        onConfirmAutoTags([getLabelInfoForDecision(label, labels)]);
                    }}
                />

                <IconButton
                    disabled={isExtractionLabelerDisabled}
                    iconProps={{ iconName: 'ErrorBadge' }}
                    styles={{
                        root: { color: theme.rejectTagsButtonColor, padding: 0, height: 'auto', width: 'auto' },
                        icon: { margin: 0 }
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        logButtonClick(
                            getPortalFromUrl().serviceName,
                            'DataLabeling',
                            entityLabel.labelType === 'autoTagged' ? 'RejectAutolabel' : 'RejectGptLabel',
                            null,
                            projectId
                        );
                        onRemoveLabel(getLabelInfoForDecision(label, labels));
                    }}
                />
                <div data-autotag-index={index} />
            </Stack>
        </TooltipHost>
    );
};

export const onTokenRender = ({
    tokenProps,
    defaultRenderer,
    isExtractionLabelerDisabled,
    ...rest
}: {
    projectId: string;
    labels: EntityLabel[];
    isExtractionLabelerDisabled: boolean;
    onRemoveLabel: (label: PartialEntityLabel) => void;
    onConfirmAutoTags: (labels: PartialEntityLabel[]) => void;
    tokenProps: ITokenRendererProps<ExtractionLabelerTokenStore>;
    defaultRenderer: (tokenProps: ITokenRendererProps<ExtractionLabelerTokenStore>) => React.ReactNode;
}) => {
    const { tokenStore } = tokenProps;
    const tokenDataAttrObj = { [tokenDataAttribute]: true, [tokenIndexDataAttribute]: `${tokenStore.index}` };

    return (
        <span className="inner-token">
            <span {...tokenDataAttrObj}>
                <Stack horizontal tokens={{ padding: '0 0 2px 0' }}>
                    {defaultRenderer(tokenProps)}

                    {tokenStore.label?.viewOptions?.isDashed &&
                        onRenderAnnotationDecisionActions({
                            label: tokenStore.label,
                            index: tokenStore.index,
                            isExtractionLabelerDisabled,
                            ...rest
                        })}
                </Stack>
            </span>
        </span>
    );
};
